import { useState } from "react";
import {
  Timestamp,
  uuid,
  type Trip,
  type TripPoll,
} from "@travelix/core";
import { Plus, Vote, X, CheckCircle2, Lock } from "lucide-react";
import type { ReactNode } from "react";

import { useAuth } from "@/lib/auth-provider";
import { EmptyState } from "@/components/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetBody, SheetFooter } from "@/components/ui/sheet";
import { Field } from "@/components/ui/field";
import { ConfirmDialog } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  addTripPoll,
  castPollVote,
  closeTripPoll,
  removeTripPoll,
  unwrap,
  queryKeys,
  displayNameOf,
} from "@/lib/api";
import { useTripMutation } from "@/hooks/use-trip-mutation";

const MAX_OPTIONS = 4;
const MIN_OPTIONS = 2;

/**
 * Polls — lightweight group decisions ("beach or museum?").
 * Single-choice for MVP; last vote wins.
 *
 * Open polls sort newest-first at the top; closed polls fall to a
 * "Decided" section below.
 */
export function PollsSection({
  trip,
  adding,
  onCloseAdd,
  readOnly = false,
}: {
  trip: Trip;
  adding: boolean;
  onCloseAdd: () => void;
  /** Past trips: no create, no vote, no close/delete. */
  readOnly?: boolean;
}) {
  const { user } = useAuth();
  const [pendingClose, setPendingClose] = useState<TripPoll | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TripPoll | null>(null);

  const polls = trip.polls ?? [];
  const open = polls
    .filter((p) => !p.closed)
    .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
  const closed = polls
    .filter((p) => !!p.closed)
    .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());

  const vote = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: async (args: { pollId: string; optionId: string }) => {
      if (!user?.uid) throw new Error("Not signed in.");
      await unwrap(castPollVote(trip.id, args.pollId, user.uid, args.optionId));
    },
    errorToast: "Couldn't vote.",
  });

  const closePoll = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: (pollId: string) => unwrap(closeTripPoll(trip.id, pollId)),
    successToast: "Poll closed.",
    errorToast: "Couldn't close.",
  });

  const deletePoll = useTripMutation({
    keys: [queryKeys.trip(trip.id)],
    mutationFn: (pollId: string) => unwrap(removeTripPoll(trip.id, pollId)),
    successToast: "Poll deleted.",
    errorToast: "Couldn't delete.",
  });

  return (
    <>
      {polls.length === 0 ? (
        <EmptyState
          icon={Vote}
          title="Nothing to decide yet"
          body="Kick off a poll when the group can't agree. Two-tap for everyone."
        />
      ) : null}

      {open.length > 0 ? (
        <section>
          <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
            Open · {open.length}
          </p>
          <div className="flex flex-col gap-3">
            {open.map((poll) => (
              <PollCard
                key={poll.id}
                poll={poll}
                currentUid={user?.uid ?? null}
                onVote={readOnly ? () => undefined : (optionId) => vote.mutate({ pollId: poll.id, optionId })}
                onClose={readOnly ? () => undefined : () => setPendingClose(poll)}
                onDelete={readOnly ? () => undefined : () => setPendingDelete(poll)}
                lockedForReadOnly={readOnly}
              />
            ))}
          </div>
        </section>
      ) : null}

      {closed.length > 0 ? (
        <section>
          <p className="mb-2 px-1 text-[11px] font-bold uppercase tracking-widest text-ink-subtle">
            Decided · {closed.length}
          </p>
          <div className="flex flex-col gap-3">
            {closed.map((poll) => (
              <PollCard
                key={poll.id}
                poll={poll}
                currentUid={user?.uid ?? null}
                onVote={() => undefined}
                onClose={() => undefined}
                onDelete={readOnly ? () => undefined : () => setPendingDelete(poll)}
                lockedForReadOnly={readOnly}
              />
            ))}
          </div>
        </section>
      ) : null}

      {adding && !readOnly ? (
        <CreatePollSheet tripId={trip.id} onClose={onCloseAdd} />
      ) : null}

      <ConfirmDialog
        open={!!pendingClose}
        onOpenChange={(v) => !v && setPendingClose(null)}
        title="Close this poll?"
        description="Nobody else can vote after this. The result stays visible."
        confirmLabel="Close poll"
        onConfirm={async () => {
          if (pendingClose) await closePoll.mutateAsync(pendingClose.id);
          setPendingClose(null);
        }}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(v) => !v && setPendingDelete(null)}
        title="Delete this poll?"
        description="It disappears for everyone, along with the votes."
        confirmLabel="Delete"
        destructive
        onConfirm={async () => {
          if (pendingDelete) await deletePoll.mutateAsync(pendingDelete.id);
          setPendingDelete(null);
        }}
      />
    </>
  );
}

// ─── Card ────────────────────────────────────────────────────
function PollCard({
  poll,
  currentUid,
  onVote,
  onClose,
  onDelete,
  lockedForReadOnly = false,
}: {
  poll: TripPoll;
  currentUid: string | null;
  onVote: (optionId: string) => void;
  onClose: () => void;
  onDelete: () => void;
  /** True when the trip is past — treats the poll as un-actionable. */
  lockedForReadOnly?: boolean;
}) {
  const myVote = currentUid ? poll.votes[currentUid] : undefined;
  const totalVotes = Object.keys(poll.votes).length;
  const isCreator = currentUid === poll.createdBy && !lockedForReadOnly;
  const isClosed = !!poll.closed;
  // Combine the two "locked" reasons — if either is true, no voting.
  const locked = isClosed || lockedForReadOnly;

  // Winning option (only meaningful when closed AND has votes)
  const winnerId = isClosed && totalVotes > 0 ? computeWinner(poll) : null;

  return (
    <article
      className={cn(
        "rounded-xl border p-4 shadow-warm-1 transition-opacity",
        isClosed ? "border-line bg-bg-elevated opacity-80" : "border-line bg-bg-elevated",
      )}
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h3
            className="font-display text-lg leading-tight tracking-tight text-ink m-0"
            style={{ fontVariationSettings: `"opsz" 36, "wght" 500` }}
          >
            {poll.question}
          </h3>
          <p className="mt-1 text-[12px] text-ink-subtle m-0 truncate">
            {isClosed ? "Closed · " : ""}
            {poll.createdByName} · {totalVotes} vote{totalVotes === 1 ? "" : "s"}
          </p>
        </div>
        {isClosed ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-bg-sunken px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-ink-subtle">
            <Lock className="h-2.5 w-2.5" />
            Closed
          </span>
        ) : null}
      </div>

      <ul className="flex flex-col gap-1.5">
        {poll.options.map((opt) => {
          const count = countVotes(poll, opt.id);
          const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          const isMine = myVote === opt.id;
          const isWinner = winnerId === opt.id;
          return (
            <li key={opt.id}>
              <button
                type="button"
                disabled={locked}
                onClick={() => !locked && onVote(opt.id)}
                className={cn(
                  "relative w-full overflow-hidden rounded-lg border px-3 py-2.5 text-left transition-colors",
                  isMine
                    ? "border-primary bg-primary-soft/60 text-ink"
                    : isWinner
                      ? "border-success/50 bg-success-soft/40 text-ink"
                      : "border-line bg-bg-sunken text-ink hover:bg-bg-sunken/60",
                  locked && "cursor-not-allowed",
                )}
              >
                {/* Vote bar behind the label */}
                <div
                  className={cn(
                    "absolute inset-y-0 left-0 transition-[width]",
                    isMine ? "bg-primary/20" : isWinner ? "bg-success/15" : "bg-ink-subtle/10",
                  )}
                  style={{ width: `${pct}%` }}
                  aria-hidden
                />
                <div className="relative flex items-center justify-between gap-3">
                  <span
                    className={cn(
                      "text-[14px] font-medium truncate flex items-center gap-1.5",
                      isMine && "font-semibold",
                    )}
                  >
                    {isMine ? <CheckCircle2 className="h-3.5 w-3.5 text-primary" /> : null}
                    {opt.label}
                  </span>
                  <span className="shrink-0 tabular text-[12px] font-semibold text-ink-muted">
                    {count} · {pct}%
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {isCreator ? (
        <div className="mt-3 flex justify-end gap-1">
          {!isClosed ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-ink-muted"
            >
              Close poll
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="text-danger hover:bg-danger-soft/40"
          >
            <X className="h-3.5 w-3.5" />
            Delete
          </Button>
        </div>
      ) : null}
    </article>
  );
}

function countVotes(poll: TripPoll, optionId: string): number {
  let n = 0;
  for (const v of Object.values(poll.votes)) if (v === optionId) n++;
  return n;
}
function computeWinner(poll: TripPoll): string | null {
  const counts = new Map<string, number>();
  for (const v of Object.values(poll.votes)) counts.set(v, (counts.get(v) ?? 0) + 1);
  let best: string | null = null;
  let bestN = -1;
  for (const [k, n] of counts) if (n > bestN) { best = k; bestN = n; }
  return best;
}

// ─── Create sheet ────────────────────────────────────────────
function CreatePollSheet({
  tripId,
  onClose,
}: {
  tripId: string;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [err, setErr] = useState<string | null>(null);

  const trimmedOptions = () => options.map((o) => o.trim()).filter(Boolean);

  const create = useTripMutation({
    keys: [queryKeys.trip(tripId)],
    mutationFn: async () => {
      const q = question.trim();
      const opts = trimmedOptions();
      if (!q) throw new Error("Ask something first.");
      if (opts.length < MIN_OPTIONS) throw new Error("At least two options.");
      if (new Set(opts.map((o) => o.toLowerCase())).size !== opts.length) {
        throw new Error("Options must be unique.");
      }
      if (!user?.uid) throw new Error("Not signed in.");

      const poll: TripPoll = {
        id: uuid(),
        question: q,
        options: opts.map((label) => ({ id: uuid(), label })),
        votes: {},
        createdBy: user.uid,
        createdByName: displayNameOf(user),
        createdAt: Timestamp.now(),
      };
      await unwrap(addTripPoll(tripId, poll));
    },
    successToast: "Poll posted.",
    onError: (e) => setErr(e instanceof Error ? e.message : "Couldn't post."),
    onSuccess: onClose,
  });

  const setOptionAt = (i: number, v: string) => {
    setOptions((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
    if (err) setErr(null);
  };
  const addOption = () =>
    setOptions((prev) => (prev.length < MAX_OPTIONS ? [...prev, ""] : prev));
  const removeOption = (i: number) =>
    setOptions((prev) => (prev.length > MIN_OPTIONS ? prev.filter((_, idx) => idx !== i) : prev));

  return (
    <Sheet open guardUnsaved onOpenChange={(o) => !o && !create.isPending && onClose()}>
      <SheetContent>
        <SheetHeader
          title="New poll"
          description="Ask the group. Everyone votes once — last vote wins."
        />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <SheetBody className="flex flex-col gap-4">
            <Field id="poll-q" label="Question">
              <Input
                id="poll-q"
                autoFocus
                placeholder="Beach today or museum?"
                value={question}
                onChange={(e) => {
                  setQuestion(e.target.value);
                  if (err) setErr(null);
                }}
                maxLength={140}
              />
            </Field>

            <div>
              <p className="mb-1.5 text-[13px] font-medium text-ink-muted">
                Options ({options.length} of {MAX_OPTIONS})
              </p>
              <ul className="flex flex-col gap-2">
                {options.map((v, i) => (
                  <li key={i} className="flex gap-2">
                    <Input
                      type="text"
                      value={v}
                      onChange={(e) => setOptionAt(i, e.target.value)}
                      placeholder={i === 0 ? "Beach" : i === 1 ? "Museum" : `Option ${i + 1}`}
                      maxLength={60}
                      className="flex-1"
                    />
                    {options.length > MIN_OPTIONS ? (
                      <button
                        type="button"
                        onClick={() => removeOption(i)}
                        aria-label="Remove option"
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-ink-subtle hover:bg-bg-sunken hover:text-ink"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
              {options.length < MAX_OPTIONS ? (
                <button
                  type="button"
                  onClick={addOption}
                  className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-primary hover:underline"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add option
                </button>
              ) : null}
            </div>

            {err ? (
              <p role="alert" className="text-[12px] text-danger">
                {err}
              </p>
            ) : null}
          </SheetBody>
          <SheetFooter>
            <Button type="submit" block disabled={create.isPending}>
              {create.isPending ? "Posting…" : "Post poll"}
            </Button>
            <Button type="button" variant="ghost" block onClick={onClose} disabled={create.isPending}>
              Cancel
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
